<template>
  <v-col cols="12">
    <v-card>
      <v-card-title class="text-h5"> Trends Over Time </v-card-title>

      <v-card-text>
        <p class="font-italic">
          Chart data restricted to officers or directors only with transaction codes of P or S
        </p>

        <v-container fluid pa-0 mt-6>
          <v-row>
            <!-- Weekly Buy vs Sell -->
            <v-col cols="12" xl="6">
              <helpers-section-sub-header class="mb-4">
                <span v-if="chart1criteria.group_by == 'week'">Weekly</span
                ><span v-else>Yearly</span> Buy vs Sell (Open Market Transactions)
              </helpers-section-sub-header>
              <v-row>
                <v-col cols="12" sm="6" lg="4" pt-1 pb-2>
                  <v-select
                    v-model="chart1criteria.timeframe"
                    :items="chartTimelineOptions"
                    item-title="text"
                    label="Timeframe"
                    density="compact"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="6" lg="8" py-1>
                  <v-select
                    v-model="chart1criteria.sectors"
                    :items="sectorNames"
                    multiple
                    chips
                    closable-chips
                    clearable
                    density="compact"
                    label="Filter by sector"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="chart1criteria.group_by"
                    :items="['day', 'week', 'year']"
                    density="compact"
                    label="Time Interval"
                    hide-details
                  />
                </v-col>
                <v-col cols="auto">
                  <v-btn @click="Chart1LabelEnabled=!Chart1LabelEnabled;updateChart1()">Toggle Labels</v-btn>
                </v-col>
              </v-row>
              <div id="chart1" class="mt-6" />
            </v-col>

            <!-- Weekly Sell vs Buy Ratio -->
            <v-col cols="12" xl="6">
              <helpers-section-sub-header class="mb-4">
                <span v-if="chart2criteria.group_by == 'week'">Weekly</span
                ><span v-else>Yearly</span> Sell vs Buy Ratio (Open Market Transactions)
              </helpers-section-sub-header>
              <v-row>
                <v-col cols="12" sm="6" lg="4" pt-1 pb-2>
                  <v-select
                    v-model="chart2criteria.timeframe"
                    :items="chartTimelineOptions"
                    item-title="text"
                    density="compact"
                    label="Timeframe"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="chart2criteria.group_by"
                    :items="['day', 'week', 'year']"
                    density="compact"
                    label="Time Interval"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="4" lg="4" py-1>
                  <v-select
                    v-model="chart2criteria.sectors"
                    :items="sectorNames"
                    multiple
                    chips
                    closable-chips
                    clearable
                    density="compact"
                    label="Filter by sector"
                    hide-details
                  />
                </v-col>
                <v-col cols="auto">
                  <v-checkbox
                    v-model="chart2criteria.show_percent_change"
                    density="compact"
                    hide-details
                    label="Show % Change From Prior Year"
                  />
                </v-col>

                <v-col cols="auto">
                  <v-btn @click="Chart2LabelEnabled=!Chart2LabelEnabled;updateChart2()">Toggle Labels</v-btn>
                </v-col>
              </v-row>
              <div id="chart2" class="mt-6" />
            </v-col>

            <!-- Weekly Sell vs Buy $ Ratio -->
            <v-col cols="12" xl="6">
              <helpers-section-sub-header class="mb-4">
                <span v-if="chart3criteria.group_by == 'week'">Weekly</span
                ><span v-else>Yearly</span> Sell vs Buy $ amount Ratio (Open Market Transactions)
                <br />
                <small>excludes reported stock prices &gt;$10,000</small>
              </helpers-section-sub-header>
              <v-row>
                <v-col cols="12" sm="6" lg="4" pt-1 pb-2>
                  <v-select
                    v-model="chart3criteria.timeframe"
                    :items="chartTimelineOptions"
                    item-title="text"
                    density="compact"
                    label="Timeframe"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="6" lg="8" py-1>
                  <v-select
                    v-model="chart3criteria.sectors"
                    :items="sectorNames"
                    multiple
                    chips
                    closable-chips
                    clearable
                    density="compact"
                    label="Filter by sector"
                    hide-details
                  />
                </v-col>
                <v-col cols="auto">
                  <v-checkbox
                    v-model="chart3criteria.show_percent_change"
                    label="Show % Change From Prior Year"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="chart3criteria.group_by"
                    :items="['day', 'week', 'year']"
                    clearable
                    density="compact"
                    label="Time Interval"
                    hide-details
                  />
                </v-col>
                <v-col cols="auto">
                  <v-btn @click="Chart2LabelEnabled=!Chart2LabelEnabled;updateChart2()">Toggle Labels</v-btn>
                </v-col>
              </v-row>
              <div id="chart3" class="mt-6" />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-col>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import api from '@/store/api'
import debounce from 'lodash/debounce'
import Highcharts from 'highcharts'
import addMore from 'highcharts/highcharts-more'
import numbro from 'numbro'

addMore(Highcharts)

export default {
  data() {
    return {
      Chart1LabelEnabled:true,
      Chart2LabelEnabled:true,
      chartTimelineOptions: [
        { value: 1, text: 'Past 6-months' },
        { value: 2, text: 'Past year' },
        { value: 3, text: 'Past 2-years' },
        { value: 4, text: 'Past 3-years' },
        { value: 5, text: 'Past 5-years' },
        { value: 6, text: 'All (starting in 2008)' }
      ],
      chart1criteria: { chart_id: 2, timeframe: 1, sectors: [], group_by: 'week' },
      chart2criteria: {
        chart_id: 3,
        timeframe: 1,
        sectors: [],
        show_percent_change: false,
        group_by: 'week'
      },
      chart3criteria: { chart_id: 4, timeframe: 1, sectors: [], group_by: 'week' }
    }
  },
  computed: {
    sectorNames() {
      return useWwStore().sectors.map(sector => sector.name)
    }
  },

  watch: {
    chart1criteria: {
      deep: true,
      immediate: true,
      handler() {
        this.updateChart1()
      }
    },
    chart2criteria: {
      deep: true,
      immediate: true,
      handler() {
        this.updateChart2()
      }
    },
    chart3criteria: {
      deep: true,
      immediate: true,
      handler() {
        this.updateChart3()
      }
    }
  },

  mounted() {
    // useWwStore().loadSectors()
  },

  methods: {
    updateChart1: debounce(function () {
      var that=this;
      api
        .post('form4_agg_chart', this.chart1criteria)
        .then(response => {
          Highcharts.chart('chart1', {
            title: {
              text: 'Buy vs Sell'
            },
            plotOptions: {
              series: {
                grouping: false,
                dataLabels: {
                  align: 'center',
                  enabled: that.Chart1LabelEnabled
                }
              }
            },
            xAxis: {
              type: 'category',
              labels: {
                enabled: that.Chart1LabelEnabled,
                formatter() {
                  return that.Chart1LabelEnabled ? this.value : ''
                }
              }
            },
            yAxis: {
              title: {
                text: '# owners per week'
              }
            },
            tooltip: {
              formatter() {
                return this.key + '<br/>' + this.series.name + ': ' + numbro(this.y).format('(0,0a')
              }
            },
            chart: {
              type: 'column'
            },
            credits: {
              enabled: false
            },
            series: response.data
          })
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
    }, 750),

    updateChart2: debounce(function () {
      var that=this;
      api
        .post('form4_agg_chart', this.chart2criteria)
        .then(response => {
          var bull_data = []
          var bear_data = []
          var max = 0
          for (var i = 0; i < response.data.length; i++) {
            var d = response.data[i]
            bull_data.push({ name: d.name, y: 2 })
            if (d.y > max) {
              max = d.y
            }
          }
          for (i = 0; i < response.data.length; i++) {
            d = response.data[i]
            bear_data.push({ name: d.name, low: 4, high: max })
          }
          Highcharts.chart('chart2', {
            title: {
              text: 'Sell vs Buy'
            },
             plotOptions: {
              series: {
                grouping: false,
                dataLabels: {
                  align: 'center',
                  enabled: that.Chart2LabelEnabled
                }
              }
            },
            xAxis: {
              type: 'category',
              labels: {
                enabled: that.Chart2LabelEnabled,
                formatter() {
                  return that.Chart2LabelEnabled ? this.value : ''
                }
              }
            },
            tooltip: {
              formatter() {
                return (
                  this.key + '<br/>' + this.series.name + ': ' + numbro(this.y).format('(0,0.00a')
                )
              }
            },
            plotOptions: {
              area: {
                marker: {
                  enabled: false
                }
              }
            },
            yAxis: [
              {
                title: {
                  text: 'Sell/Buy Ratio'
                }
              }
            ],
            chart: {
              type: 'line'
            },
            credits: {
              enabled: false
            },
            series: [
              { name: 'Sell/Buy Ratio', connectNulls: true, data: response.data },
              {
                name: 'Bull Line',
                connectNulls: true,
                data: bull_data,
                type: 'area',
                lineColor: Highcharts.getOptions().colors[1],
                color: '#00ff99',
                dashStyle: 'Dash',
                fillOpacity: 0.5,
                marker: {
                  enabled: false
                }
              },
              {
                name: 'Bear Line',
                connectNulls: true,
                data: bear_data,
                type: 'arearange',
                color: Highcharts.getOptions().colors[5],
                dashStyle: 'Dash',
                fillOpacity: 0.5,
                marker: {
                  enabled: false
                }
              }
            ]
          })
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
    }, 750),
    updateChart3: debounce(function () {
      api
        .post('form4_agg_chart', this.chart3criteria)
        .then(response => {
          Highcharts.chart('chart3', {
            title: {
              text: 'Sell vs Buy $ Amount'
            },
            plotOptions: {
              series: {
                grouping: false,
                dataLabels: {
                  align: 'center',
                  enabled: true,

                  formatter() {
                    var n = this.y
                    if (n) {
                      n = numbro(n).format({ average: true, mantissa: 1 })
                    }
                    return n
                  }
                }
              }
            },
            xAxis: {
              type: 'category',
              labels: {
                formatter() {
                  return this.value
                }
              }
            },
            yAxis: {
              title: {
                text: '$ Transaction Amount'
              }
            },
            tooltip: {
              formatter() {
                return (
                  this.key +
                  '<br/>' +
                  this.series.name +
                  ': ' +
                  numbro(this.y).format({
                    average: true,
                    mantissa: 1
                  })
                )
              }
            },
            chart: {
              type: 'column'
            },
            credits: {
              enabled: false
            },
            series: response.data
          })
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
    }, 750)
  }
}
</script>
